import type { SpotMarketPriceResponseDto } from '@/src/api/types/generated-types'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import moment from 'moment'

export const SPOT_MARKET_SERIES_ID = 'spot_market'

type TimestampValue = string | Date
type SpotMarketPrice = Pick<SpotMarketPriceResponseDto, 'timestamp' | 'spotPrice'> &
  Partial<Pick<SpotMarketPriceResponseDto, 'timestampEnd'>>

type TimestampKeys = {
  instant: number
  wallClock: string
  wallClockInstant: number
  hasExplicitTimezone: boolean
}

const getTimestampKeys = (timestamp: TimestampValue): TimestampKeys | null => {
  const parsedTimestamp = moment.parseZone(timestamp)
  if (!parsedTimestamp.isValid()) return null
  const wallClock = parsedTimestamp.format('YYYY-MM-DD HH:mm')

  return {
    instant: parsedTimestamp.valueOf(),
    wallClock,
    wallClockInstant: moment.utc(wallClock, 'YYYY-MM-DD HH:mm').valueOf(),
    hasExplicitTimezone: timestamp instanceof Date || /(?:Z|[+-]\d{2}:\d{2})$/i.test(timestamp),
  }
}

const getSpotPriceIntervalEnd = (spotPrice: SpotMarketPrice): string => spotPrice.timestampEnd ?? spotPrice.timestamp

export const createSpotMarketPriceLookup = (spotPrices: SpotMarketPrice[]) => {
  const pricesByInstant = new Map<number, number>()
  const pricesByWallClock = new Map<string, number>()

  for (const spotPrice of spotPrices) {
    const keys = getTimestampKeys(getSpotPriceIntervalEnd(spotPrice))
    if (!keys) continue

    pricesByInstant.set(keys.instant, spotPrice.spotPrice)
    pricesByWallClock.set(keys.wallClock, spotPrice.spotPrice)
  }

  return (timestamp: TimestampValue): number | undefined => {
    const keys = getTimestampKeys(timestamp)
    if (!keys) return undefined

    return keys.hasExplicitTimezone ? pricesByInstant.get(keys.instant) : pricesByWallClock.get(keys.wallClock)
  }
}

const granularityMinutes = {
  [GranularityEnum.FIFTEEN_MINUTES]: 15,
  [GranularityEnum.THIRTY_MINUTES]: 30,
  [GranularityEnum.ONE_HOUR]: 60,
} as const

type LoadProfileGranularity = keyof typeof granularityMinutes

const createSpotMarketBucketPriceLookup = (spotPrices: SpotMarketPrice[], granularity: LoadProfileGranularity) => {
  const intervalDurationMs = granularityMinutes[granularity] * 60 * 1000
  const priceIntervals = spotPrices
    .map(spotPrice => {
      const keys = getTimestampKeys(getSpotPriceIntervalEnd(spotPrice))
      return keys ? { ...keys, spotPrice: spotPrice.spotPrice } : null
    })
    .filter((priceInterval): priceInterval is TimestampKeys & { spotPrice: number } => priceInterval !== null)

  return (timestamp: TimestampValue): number | undefined => {
    const keys = getTimestampKeys(timestamp)
    if (!keys) return undefined

    const intervalEnd = keys.hasExplicitTimezone ? keys.instant : keys.wallClockInstant
    const intervalStart = intervalEnd - intervalDurationMs
    const pricesInInterval = priceIntervals
      .filter(priceInterval => {
        const spotIntervalEnd = keys.hasExplicitTimezone ? priceInterval.instant : priceInterval.wallClockInstant
        return spotIntervalEnd > intervalStart && spotIntervalEnd <= intervalEnd
      })
      .map(priceInterval => priceInterval.spotPrice)

    if (pricesInInterval.length === 0) return undefined
    return pricesInInterval.reduce((sum, spotPrice) => sum + spotPrice, 0) / pricesInInterval.length
  }
}

type TimeSeriesDataRow = {
  date: TimestampValue
  [key: string]: unknown
}

export const alignSpotMarketSeriesRows = <T extends TimeSeriesDataRow>(
  rows: T[],
  seriesIds: string[],
  spotPrices: SpotMarketPrice[],
  granularity: string
): T[] => {
  const spotMarketSeriesKeys = seriesIds
    .map((seriesId, index) => (seriesId === SPOT_MARKET_SERIES_ID ? `timeSeries${index + 1}` : null))
    .filter((seriesKey): seriesKey is string => seriesKey !== null)

  if (spotMarketSeriesKeys.length === 0 || !(granularity in granularityMinutes)) return rows

  const getSpotPrice = createSpotMarketBucketPriceLookup(spotPrices, granularity as LoadProfileGranularity)

  return rows.map(row => {
    const alignedRow: TimeSeriesDataRow = { ...row }
    const spotPrice = getSpotPrice(row.date) ?? null

    for (const seriesKey of spotMarketSeriesKeys) {
      alignedRow[seriesKey] = spotPrice
    }

    return alignedRow as T
  })
}

export const shouldAlignSpotMarketSeries = (seriesIds: string[], granularity: string): boolean =>
  granularity in granularityMinutes && seriesIds.includes(SPOT_MARKET_SERIES_ID)

export const getSpotMarketRequestRange = (
  rows: TimeSeriesDataRow[],
  granularity: string
): { startDate: string; endDate: string } | null => {
  if (!(granularity in granularityMinutes)) return null

  const firstTimestamp = rows.find(row => getTimestampKeys(row.date))?.date
  const lastTimestamp = rows.findLast(row => getTimestampKeys(row.date))?.date
  if (!firstTimestamp || !lastTimestamp) return null

  const intervalMinutes = granularityMinutes[granularity as LoadProfileGranularity]

  return {
    startDate: moment.parseZone(firstTimestamp).subtract(intervalMinutes, 'minutes').format('YYYY-MM-DD HH:mm:ss'),
    endDate: moment.parseZone(lastTimestamp).format('YYYY-MM-DD HH:mm:ss'),
  }
}
